import React, { useState, useEffect } from 'react';
import { Building2, Save, X } from 'lucide-react';

const COLORS = [
  '#667eea', '#764ba2', '#f093fb', '#4facfe',
  '#43e97b', '#fa709a', '#fee140', '#30cfd0',
  '#a8edea', '#fed6e3', '#c471f5', '#12c2e9'
];

export default function DepartmentForm({ department, orgUnits = [], onSave, onCancel }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    responsable: '',
    ubicacion: '',
    color: '#667eea',
    mision: '',
    vision: '',
    objetivos: '',
    organizational_unit_id: ''
  });

  useEffect(() => {
    if (department) {
      setFormData({
        ...department,
        organizational_unit_id: department.organizational_unit_id || ''
      });
    }
  }, [department]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      alert('El nombre del departamento es obligatorio');
      return;
    }
    if (orgUnits.length > 0 && !formData.organizational_unit_id) {
      alert('Debe seleccionar una unidad organizativa');
      return;
    }

    onSave({ ...formData, organizational_unit_id: formData.organizational_unit_id || null });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-100">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800">
            {department ? 'Editar' : 'Nuevo'} Departamento
          </h3>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nombre del Departamento *
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="ej: Recursos Humanos, Finanzas, etc."
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
              required
            />
          </div>

          {orgUnits.length > 0 && (
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Unidad Organizativa *
              </label>
              <select
                name="organizational_unit_id"
                value={formData.organizational_unit_id}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none bg-white"
                required
              >
                <option value="">— Seleccione una unidad —</option>
                {orgUnits.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.parent_nombre ? `${u.parent_nombre} → ${u.nombre}` : u.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Responsable
            </label>
            <input
              type="text"
              name="responsable"
              value={formData.responsable}
              onChange={handleChange}
              placeholder="Nombre del responsable"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ubicación
            </label>
            <input
              type="text"
              name="ubicacion"
              value={formData.ubicacion}
              onChange={handleChange}
              placeholder="Piso, edificio, área"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Color Representativo
            </label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color }))}
                  className={`w-12 h-12 rounded-lg border-4 transition-all ${
                    formData.color === color ? 'border-gray-800 scale-110' : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción general del departamento"
              rows="3"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Misión
            </label>
            <textarea
              name="mision"
              value={formData.mision}
              onChange={handleChange}
              placeholder="Misión del departamento"
              rows="2"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Visión
            </label>
            <textarea
              name="vision"
              value={formData.vision}
              onChange={handleChange}
              placeholder="Visión del departamento"
              rows="2"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Objetivos
            </label>
            <textarea
              name="objetivos"
              value={formData.objetivos}
              onChange={handleChange}
              placeholder="Objetivos principales del departamento"
              rows="3"
              className="w-full px-4 py-3 border-2 rounded-xl focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-green-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            {department ? 'Actualizar' : 'Guardar'} Departamento
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
